import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase, isConfigured } from "./supabase";
import { AccountModal } from "./components/AccountModal";
import { PrivacyModal } from "./components/PrivacyModal";
import { SimulationModal } from "./components/SimulationModal";
import { VisitChecklistModal } from "./components/VisitChecklistModal";
import { checklistProgress } from "./utils/visitChecklistTemplates";
import { DocumentsModal } from "./components/DocumentsModal";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { BatchVerifyButton } from "./components/BatchVerifyButton";
import { ListingStatusBadge } from "./components/ListingStatusBadge";
import { logError } from "./components/ErrorBoundary";
import { track } from "./utils/track";

// ── Détection mobile ──────────────────────────────────────────────────────────
function useMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

// ── Share Target : extrait l'URL depuis les params (URL directe ou texte) ────
function getSharedUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const directUrl = params.get("url") || "";
    if (directUrl.startsWith("http")) return directUrl;
    const text = params.get("text") || params.get("title") || "";
    const match = text.match(/https?:\/\/[^\s]+/);
    if (match) return match[0];
    return null;
  } catch(e) { return null; }
}

const STATUTS_RP = ["À contacter", "Visite planifiée", "Visitée", "Offre faite", "Coup de cœur", "No-Go"];
const STATUTS_INVEST = ["À analyser", "Rendement OK", "Étude approfondie", "Offre envoyée", "Offre acceptée", "Écarté"];
const STATUTS = STATUTS_RP; // backward compat pour éventuels imports anciens
function getStatutsForUsage(usage) {
  return usage === "investissement" ? STATUTS_INVEST : STATUTS_RP;
}
const DPE_OPTIONS = ["A", "B", "C", "D", "E", "F", "G"];
const TYPES = ["Maison", "Appartement", "Villa", "Ferme", "Autre"];
const DPE_COLORS = { A: "#00b050", B: "#92d050", C: "#cccc00", D: "#ffc000", E: "#ff6600", F: "#ff0000", G: "#c00000" };
const STATUT_COLORS = {
  // RP
  "À contacter": "#6366f1", "Visite planifiée": "#f59e0b", "Visitée": "#3b82f6",
  "Offre faite": "#8b5cf6", "Coup de cœur": "#ec4899", "No-Go": "#6b7280",
  // Invest
  "À analyser": "#06b6d4", "Rendement OK": "#10b981", "Étude approfondie": "#f97316",
  "Offre envoyée": "#a855f7", "Offre acceptée": "#22c55e", "Écarté": "#9ca3af",
};
const SITES_INFO = [
  { name: "Bienici",         domain: "bienici.com",          color: "#1a73e8", icon: "⚡" },
  { name: "SeLoger",         domain: "seloger.com",           color: "#e85d04", icon: "🤖" },
  { name: "LeBonCoin",       domain: "leboncoin.fr",          color: "#ff6e26", icon: "🤖" },
  { name: "Logic-Immo",      domain: "logic-immo.com",        color: "#00b09b", icon: "🤖" },
  { name: "MeilleursAgents", domain: "meilleursagents.com",   color: "#5b4fcf", icon: "🤖" },
  { name: "PAP",             domain: "pap.fr",                color: "#c0392b", icon: "🤖" },
];
// Sites connus pour bloquer les robots (DataDome) — le fallback paste sera fréquent
const HARD_SITES = ["seloger.com", "leboncoin.fr"];

const defaultBudgets = {
  rp:  { prixAchat: "", apport: "", tauxInteret: "3.5", dureeMois: "240" },
  loc: { prixAchat: "", apport: "", tauxInteret: "4.0", dureeMois: "240" },
};
// Sélectionne le budget pertinent selon l'usage du bien.
// "investissement" → budget locatif. "residence", "mixte", undefined → budget RP.
function getBudgetForMaison(budgets, maison) {
  const usage = maison?.usage || "residence";
  return usage === "investissement" ? budgets.loc : budgets.rp;
}
const emptyMaison = {
  id: null, titre: "", type: "Maison", prix: "", surface: "", pieces: "",
  terrain: "", adresse: "", ville: "", anneeConstruction: "", dpe: "",
  agence: "", lienAnnonce: "", estimationTravaux: "", statut: "À contacter",
  photos: [], commentaires: "", notesVisite: "",
  dateAjout: "", datePublication: "", dateVisite: "",
  // Champs spécifiques investissement locatif (visibles uniquement si usage === "investissement")
  usage: "residence", loyerEstime: "", loyerM2Secteur: "", tensionLocative: "",
};
const geocodeCache = new Map();
let lastGeocodeCallAt = 0;
const GEOCODE_MIN_INTERVAL_MS = 1100; // respecte la limite Nominatim de 1 req/s
// Géocode une adresse via Nominatim, avec cache mémoire et throttling partagé (1 req/s).
async function geocodeAddress(key) {
  if (!key) return null;
  if (geocodeCache.has(key)) return geocodeCache.get(key);
  const wait = GEOCODE_MIN_INTERVAL_MS - (Date.now() - lastGeocodeCallAt);
  if (wait > 0) await new Promise(res => setTimeout(res, wait));
  lastGeocodeCallAt = Date.now();
  let coords = null;
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(key + ', France')}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'fr-FR,fr' } }
    );
    const data = await r.json();
    if (data?.[0]) coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch (e) {}
  geocodeCache.set(key, coords);
  return coords;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatEur(v) {
  if (!v && v !== 0) return "—";
  return Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}
function calcPrixM2(prix, surface) {
  if (!prix || !surface || Number(surface) === 0) return null;
  return Math.round(Number(prix) / Number(surface));
}
function calcMensualite(prix, travaux, apport, taux, duree) {
  const montant = (Number(prix) + Number(travaux || 0)) - Number(apport || 0);
  if (!montant || !taux || !duree || montant <= 0) return null;
  const r = Number(taux) / 100 / 12;
  const n = Number(duree);
  if (r === 0) return montant / n;
  return (montant * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}
function detectSite(url) {
  return SITES_INFO.find(s => url.includes(s.domain)) || null;
}
function today() {
  return new Date().toLocaleDateString("fr-FR");
}
function getImportFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("import");
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(raw)))));
  } catch(e) { return null; }
}
function buildMaisonFromData(data) {
  const maison = { ...emptyMaison, ...data, id: Date.now(), dateAjout: today() };
  Object.keys(maison).forEach(k => { if (maison[k] === null || maison[k] === undefined) maison[k] = ""; });
  if (!Array.isArray(maison.photos)) maison.photos = [];
  return maison;
}

// ── Import Bienici API ────────────────────────────────────────────────────────
async function importFromBienici(url) {
  const clean = url.split("?")[0];
  const id = clean.split("/").filter(Boolean).pop();
  if (!id) throw new Error("Impossible d'extraire l'identifiant Bienici");

  const res = await fetch(`https://www.bienici.com/realEstateAd.json?id=${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`API Bienici inaccessible (${res.status})`);
  const d = await res.json();

  const dpeRaw = d.energyClassification || d.dpe || d.energyRate;
  const dpeMap = { 1:"A", 2:"B", 3:"C", 4:"D", 5:"E", 6:"F", 7:"G" };
  const dpe = typeof dpeRaw === "string" && /^[A-G]$/i.test(dpeRaw.trim())
    ? dpeRaw.trim().toUpperCase()
    : dpeMap[dpeRaw] || "";
  const annee = d.constructionYear || d.yearOfConstruction || d.buildYear || "";
  const terrain = d.landSurfaceArea || d.landArea || d.plotArea || "";
  const photo = d.photos?.[0]?.url
    || d.photos?.[0]?.url_photo
    || d.photos?.[0]?.thumbnail_url
    || d.mainPhoto?.url
    || null;
  const agence = d.contactRelativeData?.agencyNameToDisplay
    || d.contactRelativeData?.contactNameToDisplay
    || d.agencyName
    || d.agency?.name
    || "";
  let datePub = "";
  if (d.publicationDate || d.modificationDate) {
    try { datePub = new Date(d.publicationDate || d.modificationDate).toLocaleDateString("fr-FR"); } catch(e) {}
  }

  return {
    titre: d.title || d.description?.slice(0, 60) || "",
    type: d.propertyType === "house" ? "Maison" : d.propertyType === "flat" ? "Appartement" : "Maison",
    prix: d.price ? String(d.price) : "",
    surface: d.surfaceArea ? String(d.surfaceArea) : "",
    pieces: d.roomsQuantity ? String(d.roomsQuantity) : "",
    terrain: terrain ? String(terrain) : "",
    adresse: d.address?.street || d.address?.label || "",
    ville: d.city || d.address?.city || "",
    anneeConstruction: annee ? String(annee) : "",
    dpe,
    agence,
    commentaires: d.description || "",
    datePublication: datePub,
    photos: photo ? [photo] : [],
    lienAnnonce: url,
  };
}

// ── Import via /api/extract (fetch HTML côté serveur + Claude Haiku) ─────────
// mode URL : le serveur télécharge la page. mode paste : le texte est envoyé.
async function importFromServer(url, pastedText) {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error("Session expirée, reconnecte-toi puis réessaie.");

  const response = await fetch("/api/extract", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
    body: JSON.stringify(pastedText ? { url, pastedText } : { url }),
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || "Erreur serveur");
    err.blocked = !!data.blocked;
    throw err;
  }
  return data;
}

// ── Composant : Preview éditable ──────────────────────────────────────────────
function EditablePreview({ preview, setPreview, onConfirm, onReset }) {
  const set = (k, v) => setPreview(p => ({ ...p, [k]: v }));
  const prixM2 = calcPrixM2(preview.prix, preview.surface);
  const inp = {
    background:"rgba(0,0,0,0.5)", border:"1px solid rgba(255,255,255,0.12)",
    borderRadius:7, padding:"8px 10px", color:"#fff", fontSize:13, outline:"none", width:"100%", boxSizing:"border-box"
  };
  return (
    <div style={{ marginTop:4 }}>
      <div style={{ fontSize:13, color:"#86efac", marginBottom:12 }}>✅ Données extraites — corrige si besoin puis enregistre</div>
      {preview.photos?.[0] && (
        <img src={preview.photos[0]} alt="" onError={e=>e.target.style.display="none"}
          style={{ width:"100%", height:130, objectFit:"cover", borderRadius:10, marginBottom:10 }} />
      )}
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>Titre</div>
        <input value={preview.titre||""} onChange={e=>set("titre",e.target.value)} style={inp} />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
        {[["Prix (€)","prix"],["Surface (m²)","surface"],["Pièces","pieces"]].map(([label,k])=>(
          <div key={k}>
            <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>{label}</div>
            <input type="text" inputMode="numeric" value={preview[k]||""} onChange={e=>set(k,e.target.value.replace(/\D/g,""))} style={inp} />
          </div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
        <div>
          <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>Terrain (m²)</div>
          <input type="text" inputMode="numeric" value={preview.terrain||""} onChange={e=>set("terrain",e.target.value.replace(/\D/g,""))} style={inp} />
        </div>
        <div>
          <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>Année</div>
          <input type="text" inputMode="numeric" maxLength={4} value={preview.anneeConstruction||""} onChange={e=>set("anneeConstruction",e.target.value.replace(/\D/g,""))} style={inp} placeholder="ex: 1985" />
        </div>
        <div>
          <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>Prix/m²</div>
          <div style={{ ...inp, color: prixM2?"#a78bfa":"#4b5563", fontWeight:700, opacity:0.8 }}>
            {prixM2 ? formatEur(prixM2) : "Auto"}
          </div>
        </div>
      </div>
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:6 }}>DPE</div>
        <div style={{ display:"flex", gap:5 }}>
          {DPE_OPTIONS.map(d => (
            <button key={d} onClick={()=>set("dpe", preview.dpe===d?"":d)}
              style={{ flex:1, padding:"7px 0", borderRadius:6, border:"2px solid", borderColor: preview.dpe===d?DPE_COLORS[d]:"transparent", background: preview.dpe===d?DPE_COLORS[d]+"33":"rgba(0,0,0,0.3)", color: preview.dpe===d?DPE_COLORS[d]:"#6b7280", fontWeight:700, cursor:"pointer", fontSize:12 }}>
              {d}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
        {[["Ville","ville"],["Agence","agence"]].map(([label,k])=>(
          <div key={k}>
            <div style={{ fontSize:10, color:"#9ca3af", textTransform:"uppercase", marginBottom:4 }}>{label}</div>
            <input value={preview[k]||""} onChange={e=>set(k,e.target.value)} style={inp} />
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={onReset} style={{ flex:1, padding:"11px", borderRadius:9, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"#9ca3af", cursor:"pointer", fontWeight:600 }}>↩ Recommencer</button>
        <button onClick={onConfirm} style={{ flex:2, padding:"11px", borderRadius:9, border:"none", background:"linear-gradient(135deg,#16a34a,#4ade80)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:14 }}>💾 Enregistrer la fiche</button>
      </div>
    </div>
  );
}

// ── Composant : Import Modal ──────────────────────────────────────────────────
function ImportModal({ onImport, onClose, initialUrl = "" }) {
  const isMobile = useMobile();
  const [url, setUrl] = useState(initialUrl);
  const [pastedText, setPastedText] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const site = url ? detectSite(url) : null;

  // Auto-lancer si URL reçue via Share Target
  useEffect(() => {
    if (initialUrl && initialUrl.startsWith("http")) {
      handleImport(initialUrl);
    }
  }, []); // eslint-disable-line

  const handleImport = async (targetUrl) => {
    const importUrl = targetUrl || url;
    if (!importUrl.startsWith("http")) { setError("Colle une URL complète (https://)"); return; }
    if (showPaste && !pastedText.trim()) { setError("Colle le texte de l'annonce"); return; }
    setStatus("loading"); setError("");
    try {
      let data;
      if (importUrl.includes("bienici.com")) {
        data = await importFromBienici(importUrl);
      } else if (showPaste) {
        data = await importFromServer(importUrl, pastedText);
      } else {
        data = await importFromServer(importUrl, null);
      }
      setPreview(data);
      setStatus("success");
    } catch(e) {
      console.error("Import error:", e);
      if (e.blocked) {
        // Site protégé anti-bot : bascule automatique sur le copier-coller
        setShowPaste(true);
        setStatus("idle");
        setError(e.message);
      } else {
        setError(e.message);
        setStatus("error");
      }
    }
  };

  const handleConfirm = () => {
    const maison = buildMaisonFromData({ ...preview, lienAnnonce: url || initialUrl });
    onImport(maison);
  };

  const reset = () => { setStatus("idle"); setPreview(null); setError(""); setPastedText(""); setShowPaste(false); };

  const inp = { background:"rgba(0,0,0,0.4)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"12px 14px", color:"#fff", fontSize:14, outline:"none", width:"100%", boxSizing:"border-box" };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:200, display:"flex", alignItems: isMobile?"flex-end":"center", justifyContent:"center", padding: isMobile?0:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#0f0f13", border:"1px solid rgba(255,255,255,0.12)", borderRadius: isMobile?"20px 20px 0 0":"20px", maxWidth:640, width:"100%", maxHeight: isMobile?"92vh":"90vh", overflow:"auto" }}>
        <div style={{ padding:"24px 28px 0", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:"#f1f5f9" }}>✨ Importer une annonce</h2>
            <p style={{ margin:"6px 0 0", fontSize:13, color:"#6b7280" }}>Bienici : API directe ⚡ — Autres sites : extraction serveur 🤖</p>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.06)", border:"none", color:"#9ca3af", width:32, height:32, borderRadius:"50%", fontSize:16, cursor:"pointer" }}>×</button>
        </div>

        <div style={{ padding:"14px 28px 0", display:"flex", flexWrap:"wrap", gap:7 }}>
          {SITES_INFO.map(s => {
            const active = site?.domain === s.domain;
            return <span key={s.name} style={{ fontSize:12, padding:"4px 10px", borderRadius:99, background: active ? s.color+"22":"rgba(255,255,255,0.04)", border:`1px solid ${active ? s.color:"rgba(255,255,255,0.09)"}`, color: active ? s.color:"#555", fontWeight:600 }}>{s.icon} {s.name}</span>;
          })}
        </div>

        <div style={{ padding:"16px 28px 20px" }}>
          {/* Zone de collage universelle : URL ou texte contenant un lien */}
          {!preview && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:"#9ca3af", marginBottom:7, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Colle l'URL ou un texte contenant le lien</div>
              <textarea
                value={url}
                rows={3}
                onChange={e => {
                  const val = e.target.value;
                  const match = val.match(/https?:\/\/\S+/);
                  const extracted = match ? match[0].trim() : val.trim();
                  setUrl(extracted);
                  if (extracted !== val.trim()) reset();
                }}
                placeholder="Colle ici une URL ou un texte contenant un lien"
                style={{ ...inp, resize:"none", lineHeight:1.5, fontSize:13, borderColor: site ? site.color : "rgba(255,255,255,0.15)" }}
              />
              {site && url.startsWith("http") && (
                <div style={{ fontSize:12, color: site.color, marginTop:5 }}>
                  ✓ {site.name} détecté — {site.domain === "bienici.com" ? "import via API directe" : "extraction serveur"}
                </div>
              )}
            </div>
          )}

          {/* Info site détecté (message honnête pour les sites anti-bot) */}
          {site && !preview && !showPaste && (
            <div style={{ marginBottom:12, padding:"11px 14px", background:"rgba(26,115,232,0.08)", border:"1px solid rgba(26,115,232,0.25)", borderRadius:10, fontSize:13, color:"#93c5fd" }}>
              {site.domain === "bienici.com"
                ? "⚡ Bienici détecté — import automatique via API."
                : HARD_SITES.includes(site.domain)
                  ? `🤖 ${site.name} détecté — tentative d'import auto. Ce site bloque souvent les robots : le copier-coller sera proposé si besoin.`
                  : `🤖 ${site.name} détecté — extraction automatique de la page.`}
            </div>
          )}

          {/* Fallback paste */}
          {showPaste && !preview && (
            <div style={{ marginBottom:12 }}>
              {error && (
                <div style={{ padding:"11px 14px", background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:10, marginBottom:10, fontSize:13, color:"#fca5a5" }}>
                  ⚠️ {error}
                </div>
              )}
              <div style={{ padding:"13px 15px", background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:10, marginBottom:10 }}>
                <div style={{ fontWeight:700, color:"#fbbf24", fontSize:13, marginBottom:5 }}>📋 Comment copier le texte de l'annonce ?</div>
                <ol style={{ margin:0, paddingLeft:18, color:"#d1d5db", fontSize:13, lineHeight:1.8 }}>
                  <li>Ouvre l'annonce dans le navigateur (pas l'appli du site)</li>
                  <li>Sélectionne tout le texte de la page et copie-le</li>
                  <li>Colle-le ci-dessous</li>
                </ol>
              </div>
              <textarea value={pastedText} onChange={e => setPastedText(e.target.value)} rows={5}
                placeholder="Colle ici le texte de l'annonce..."
                style={{ ...inp, resize:"vertical", lineHeight:1.5 }} />
              {pastedText.trim() && <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>✓ {pastedText.trim().split(/\s+/).length} mots</div>}
            </div>
          )}

          {/* Bouton principal */}
          {!preview && (
            <button onClick={() => handleImport()} disabled={status==="loading" || !url || (showPaste && !pastedText.trim())}
              style={{ width:"100%", padding:"13px", borderRadius:10, border:"none", background: status==="loading" ? "rgba(167,139,250,0.3)" : "linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor: status==="loading"?"default":"pointer", fontWeight:700, fontSize:15, opacity:!url?0.4:1, marginBottom:8 }}>
              {status==="loading"
                ? <span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                    <span style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"spin 0.8s linear infinite"}} />
                    {site?.domain === "bienici.com" ? "Connexion à Bienici…" : showPaste ? "Analyse du texte…" : "Extraction de la page…"}
                  </span>
                : showPaste ? "🤖 Extraire depuis le texte" : "⚡ Importer automatiquement"}
            </button>
          )}

          {/* Erreur non-bloquante avec options */}
          {status==="error" && !preview && (
            <div style={{ marginTop:8, padding:"12px 15px", background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:10, color:"#f87171", fontSize:13 }}>
              <strong>⚠️ {error}</strong>
              <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
                <button onClick={reset} style={{ padding:"6px 12px", borderRadius:7, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"#9ca3af", cursor:"pointer", fontSize:12 }}>↩ Réessayer</button>
                {!showPaste && (
                  <button onClick={() => { setShowPaste(true); setStatus("idle"); setError(""); }}
                    style={{ padding:"6px 12px", borderRadius:7, border:"1px solid rgba(245,158,11,0.4)", background:"rgba(245,158,11,0.08)", color:"#fbbf24", cursor:"pointer", fontSize:12 }}>
                    📋 Copier-coller à la place
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Preview éditable */}
          {status==="success" && preview && (
            <EditablePreview preview={preview} setPreview={setPreview} onConfirm={handleConfirm} onReset={reset} />
          )}
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}

// ── Budget ────────────────────────────────────────────────────────────────────
function BudgetWidget({ budgets, setBudgets }) {
  const isMobile = useMobile();
  const [activeTab, setActiveTab] = useState("rp");
  const budget = budgets[activeTab];

  const setActiveBudget = (updaterFn) => setBudgets(prev => ({
    ...prev,
    [activeTab]: typeof updaterFn === "function" ? updaterFn(prev[activeTab]) : updaterFn,
  }));

  const tabStyle = (active) => ({
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid",
    borderColor: active ? "rgba(167,139,250,0.5)" : "rgba(255,255,255,0.1)",
    background: active ? "rgba(167,139,250,0.15)" : "transparent",
    color: active ? "#c4b5fd" : "#9ca3af",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    flex: 1,
  });

  return (
    <div style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:16, padding:24, marginBottom:28 }}>
      <h3 style={{ margin:"0 0 14px", fontSize:14, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#a78bfa" }}>💰 Mon Budget</h3>
      <div style={{ display:"flex", gap:8, marginBottom:18 }}>
        <button onClick={() => setActiveTab("rp")} style={tabStyle(activeTab === "rp")}>🏠 Résidence principale</button>
        <button onClick={() => setActiveTab("loc")} style={tabStyle(activeTab === "loc")}>💸 Investissement locatif</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>
        {[["Prix max d'achat (€)","prixAchat","300000"],["Apport personnel (€)","apport","50000"],["Taux d'intérêt (%)","tauxInteret","3.5"],["Durée (mois)","dureeMois","240"]].map(([label,key,ph]) => (
          <label key={key} style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
            <input type="number" value={budget[key]} placeholder={ph} onChange={e => setActiveBudget(b=>({...b,[key]:e.target.value}))}
              style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:14, outline:"none" }} />
          </label>
        ))}
      </div>
      {budget.prixAchat && (
        <div style={{ marginTop:14, padding:"12px 16px", background:"rgba(167,139,250,0.1)", borderRadius:10, border:"1px solid rgba(167,139,250,0.3)" }}>
          <span style={{ fontSize:12, color:"#a78bfa" }}>Mensualité estimée : </span>
          <strong style={{ color:"#fff", fontSize:16 }}>
            {calcMensualite(budget.prixAchat,0,budget.apport,budget.tauxInteret,budget.dureeMois)
              ? formatEur(calcMensualite(budget.prixAchat,0,budget.apport,budget.tauxInteret,budget.dureeMois))+"/mois" : "—"}
          </strong>
        </div>
      )}
    </div>
  );
}

function BudgetBar({ maison, budget }) {
  const total = Number(maison.prix||0) + Number(maison.estimationTravaux||0);
  const max = Number(budget.prixAchat||0);
  if (!total || !max) return null;
  const over = total > max;
  const mensualite = calcMensualite(maison.prix, maison.estimationTravaux, budget.apport, budget.tauxInteret, budget.dureeMois);
  return (
    <div style={{ marginTop:10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4, color: over?"#f87171":"#86efac" }}>
        <span>{formatEur(total)}</span>
        <span>{over ? `⚠️ +${formatEur(total-max)}` : `✓ -${formatEur(max-total)}`}</span>
      </div>
      <div style={{ height:5, background:"rgba(255,255,255,0.1)", borderRadius:99, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${Math.min((total/max)*100,100)}%`, background: over?"#ef4444":"#22c55e", borderRadius:99 }} />
      </div>
      {mensualite && <div style={{ marginTop:5, fontSize:11, color:"#c4b5fd" }}>~{formatEur(mensualite)}/mois</div>}
    </div>
  );
}

// ── Photos ────────────────────────────────────────────────────────────────────
function PhotoUpload({ photos, onChange }) {
  const inputRef = useRef();
  const handleFiles = e => Array.from(e.target.files).forEach(file => {
    const reader = new FileReader();
    reader.onload = ev => onChange(prev => [...prev, ev.target.result]);
    reader.readAsDataURL(file);
  });
  return (
    <div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:8 }}>
        {photos.map((p,i) => (
          <div key={i} style={{ position:"relative" }}>
            <img src={p} alt="" style={{ width:88, height:66, objectFit:"cover", borderRadius:8 }} />
            <button onClick={() => onChange(prev => prev.filter((_,j)=>j!==i))}
              style={{ position:"absolute", top:-5, right:-5, background:"#ef4444", border:"none", color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:10, cursor:"pointer" }}>×</button>
          </div>
        ))}
        <button onClick={() => inputRef.current.click()} style={{ width:88, height:66, border:"2px dashed rgba(255,255,255,0.2)", borderRadius:8, background:"transparent", color:"#6b7280", cursor:"pointer", fontSize:20 }}>+</button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display:"none" }} />
    </div>
  );
}

// ── MapView ───────────────────────────────────────────────────────────────────
function MapView({ maisons, onSelectMaison, isMobile, onGeocoded }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geoProgress, setGeoProgress] = useState({ done: 0, total: 0 });
  const [geocoded, setGeocoded] = useState([]);
  const [preview, setPreview] = useState(null);
  const [filterStatut, setFilterStatut] = useState("Tous");

  const candidates = useMemo(
    () => maisons.filter(m => m.adresse || m.ville),
    [maisons]
  );
  const candidateKey = useMemo(
    () => candidates.map(m => `${m.id}|${m.adresse}|${m.ville}`).join(','),
    [candidates]
  );

  const visible = useMemo(
    () => filterStatut === "Tous" ? geocoded : geocoded.filter(g => g.maison.statut === filterStatut),
    [geocoded, filterStatut]
  );

  // Géocodage : les biens déjà géolocalisés s'affichent immédiatement,
  // seuls ceux sans coordonnées sont envoyés à Nominatim (1 req/s).
  useEffect(() => {
    if (!candidates.length) { setGeocoded([]); setGeocoding(false); setGeoProgress({ done:0, total:0 }); return; }
    let cancelled = false;

    const already = candidates
      .filter(m => m.lat != null && m.lng != null)
      .map(m => ({ maison: m, lat: m.lat, lng: m.lng }));
    const pending = candidates.filter(m => m.lat == null || m.lng == null);

    setGeocoded(already);

    if (!pending.length) { setGeocoding(false); setGeoProgress({ done:0, total:0 }); return; }

    (async () => {
      setGeocoding(true);
      setGeoProgress({ done: 0, total: pending.length });
      for (let i = 0; i < pending.length; i++) {
        if (cancelled) break;
        const m = pending[i];
        const key = [m.adresse, m.ville].filter(Boolean).join(', ');
        const coords = await geocodeAddress(key);
        if (cancelled) break;
        if (coords) {
          setGeocoded(prev => [...prev, { maison: m, ...coords }]);
          onGeocoded?.({ ...m, lat: coords.lat, lng: coords.lng });
        }
        setGeoProgress({ done: i + 1, total: pending.length });
      }
      if (!cancelled) setGeocoding(false);
    })();
    return () => { cancelled = true; };
  }, [candidateKey]); // eslint-disable-line

  // Init Leaflet — centré par défaut sur l'Île-de-France, jamais la France entière
  useEffect(() => {
    let retries = 0;
    const tryInit = () => {
      if (mapRef.current || !mapContainerRef.current) return;
      if (!window.L) { if (retries++ < 20) setTimeout(tryInit, 200); return; }
      mapRef.current = window.L.map(mapContainerRef.current, { center: [48.8566, 2.3522], zoom: 9 });
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://openstreetmap.org" target="_blank">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(mapRef.current);
      setGeocoded(g => [...g]);
    };
    tryInit();
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []); // eslint-disable-line

  // Marqueurs selon filtre
  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    map.eachLayer(l => { if (l instanceof L.Marker) map.removeLayer(l); });
    if (!visible.length) return;
    const bounds = [];
    visible.forEach(({ maison, lat, lng }) => {
      const color = STATUT_COLORS[maison.statut] || '#6b7280';
      const icon = L.divIcon({
        html: `<div style="width:30px;height:30px;background:${color};border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)"></div>`,
        iconSize: [30, 38], iconAnchor: [15, 38], className: ''
      });
      L.marker([lat, lng], { icon })
        .addTo(map)
        .on('click', e => { L.DomEvent.stopPropagation(e); setPreview(maison); });
      bounds.push([lat, lng]);
    });
    if (bounds.length === 1) map.setView(bounds[0], 14);
    else if (bounds.length > 1) map.fitBounds(bounds, { padding: [50, 50] });
  }, [visible]);

  // Clic fond carte → ferme preview
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = () => setPreview(null);
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [geocoded.length]);

  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [geocoded.length]);

  if (!candidates.length) return (
    <div style={{ textAlign:'center', padding:'60px 20px', color:'#4b5563' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>🗺️</div>
      <div style={{ fontWeight:700, color:'#6b7280', marginBottom:6 }}>Aucune annonce à afficher</div>
      <div style={{ fontSize:12, color:'#4b5563' }}>Renseigne l'adresse ou la ville dans tes annonces pour les voir ici</div>
    </div>
  );

  const prixM2preview = preview ? calcPrixM2(preview.prix, preview.surface) : null;
  const BOTTOM_NAV_H = 64;

  return (
    <div style={{ position:'relative', marginBottom: isMobile ? 0 : 16 }}>

      {/* ── Bandeau supérieur : progression géocodage + filtre statut ── */}
      <div style={{ background:'rgba(15,15,19,0.95)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'12px 12px 0 0', padding:'10px 14px', display:'flex', flexDirection:'column', gap:8 }}>

        {/* Progression du géocodage */}
        {geocoding ? (
          <span style={{ fontSize:11, color:'#a78bfa', display:'flex', gap:6, alignItems:'center', whiteSpace:'nowrap' }}>
            <span style={{ width:10, height:10, border:'2px solid rgba(167,139,250,0.3)', borderTopColor:'#a78bfa', borderRadius:'50%', display:'inline-block', animation:'spin 0.8s linear infinite' }} />
            Géolocalisation {geoProgress.done}/{geoProgress.total}…
          </span>
        ) : (
          <span style={{ fontSize:10, color:'#6b7280', whiteSpace:'nowrap' }}>{geocoded.length}/{candidates.length} géolocalisées</span>
        )}

        {/* Filtre statut */}
        <div style={{ display:'flex', gap:5, overflowX:'auto', paddingBottom:2 }}>
          {["Tous", ...STATUTS].map(s => {
            const active = filterStatut === s;
            const color = s === "Tous" ? "#a78bfa" : STATUT_COLORS[s];
            const count = s === "Tous" ? geocoded.length : geocoded.filter(g => g.maison.statut === s).length;
            if (s !== "Tous" && count === 0) return null;
            return (
              <button key={s} onClick={() => { setFilterStatut(s); setPreview(null); }}
                style={{ padding:'5px 11px', borderRadius:99, border:'1px solid', flexShrink:0,
                  borderColor: active ? color : 'rgba(255,255,255,0.1)',
                  background: active ? color+'22' : 'transparent',
                  color: active ? color : '#6b7280',
                  cursor:'pointer', fontWeight:700, fontSize:11, whiteSpace:'nowrap',
                  display:'flex', gap:5, alignItems:'center'
                }}>
                {s}
                <span style={{ background: active ? color+'33' : 'rgba(255,255,255,0.08)', borderRadius:99, padding:'0 5px', fontSize:10 }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Carte ── */}
      <div ref={mapContainerRef}
        style={{ width:'100%', height: isMobile ? `calc(100dvh - 228px)` : 'calc(100dvh - 260px)', minHeight:360, borderRadius:'0 0 12px 12px', overflow:'hidden' }}
      />

      {/* ── Mini-fiche preview : fixed au bas du viewport ── */}
      {preview && (
        <div style={{ position:'fixed', bottom: isMobile ? BOTTOM_NAV_H + 8 : 16, left:16, right:16, zIndex:1500,
          background:'#0f0f13', border:'1px solid rgba(255,255,255,0.15)', borderRadius:16,
          overflow:'hidden', boxShadow:'0 8px 40px rgba(0,0,0,0.8)',
          maxWidth: isMobile ? undefined : 480, margin: isMobile ? undefined : '0 auto'
        }} onClick={e => e.stopPropagation()}>
          <div style={{ display:'flex' }}>
            {/* Photo */}
            <div style={{ width:110, minHeight:88, background:'rgba(0,0,0,0.4)', flexShrink:0, position:'relative', overflow:'hidden' }}>
              {preview.photos?.[0]
                ? <img src={preview.photos[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', position:'absolute', inset:0 }} onError={e=>e.target.style.display='none'} />
                : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, position:'absolute', inset:0 }}>🏡</div>}
              {preview.dpe && (
                <span style={{ position:'absolute', bottom:4, left:4, background:DPE_COLORS[preview.dpe], color:'#000', fontWeight:800, fontSize:9, borderRadius:4, padding:'1px 5px' }}>{preview.dpe}</span>
              )}
            </div>
            {/* Infos */}
            <div style={{ flex:1, padding:'10px 12px', minWidth:0 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6, marginBottom:3 }}>
                <div style={{ fontWeight:800, fontSize:13, color:'#f1f5f9', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                  {preview.titre || `${preview.type} · ${preview.ville}`}
                </div>
                <button onClick={() => setPreview(null)}
                  style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'#9ca3af', width:22, height:22, borderRadius:'50%', fontSize:12, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:5, flexWrap:'wrap' }}>
                <span style={{ fontSize:15, fontWeight:800, color:'#a78bfa' }}>{preview.prix ? Number(preview.prix).toLocaleString('fr-FR')+'€' : '—'}</span>
                {prixM2preview && <span style={{ fontSize:10, color:'#6b7280' }}>{prixM2preview.toLocaleString('fr-FR')}€/m²</span>}
                <span style={{ fontSize:10, background:STATUT_COLORS[preview.statut]+'22', color:STATUT_COLORS[preview.statut], borderRadius:5, padding:'1px 6px', fontWeight:700, border:`1px solid ${STATUT_COLORS[preview.statut]}44`, whiteSpace:'nowrap' }}>{preview.statut}</span>
              </div>
              <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:8 }}>
                {[preview.surface&&`${preview.surface}m²`, preview.pieces&&`${preview.pieces}p`, preview.ville].filter(Boolean).map((t,i) => (
                  <span key={i} style={{ fontSize:10, background:'rgba(255,255,255,0.06)', borderRadius:4, padding:'1px 6px', color:'#9ca3af' }}>{t}</span>
                ))}
              </div>
              <button onClick={() => { setPreview(null); onSelectMaison(preview); }}
                style={{ width:'100%', padding:'7px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#7c3aed,#a78bfa)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:12 }}>
                Voir la fiche complète →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Formulaire ────────────────────────────────────────────────────────────────
function MaisonForm({ initial, onSave, onCancel }) {
  const [m, setM] = useState(() => initial || { ...emptyMaison, id: Date.now(), dateAjout: today() });
  const set = (k, v) => setM(prev => ({ ...prev, [k]: v }));
  const prixM2 = calcPrixM2(m.prix, m.surface);
  const inp = { background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:14, outline:"none" };
  const Field = ({ label, k, type="text", placeholder="", readOnly=false }) => (
    <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
      <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
      <input type={type} value={m[k]||""} placeholder={placeholder} readOnly={readOnly}
        onChange={e => !readOnly && set(k, e.target.value)}
        style={{ ...inp, ...(readOnly ? { opacity:0.6, cursor:"default" } : {}) }} />
    </label>
  );
  const isImported = !!(initial?.commentaires || initial?.agence);

  return (
    <div style={{ padding:28 }}>
      <style>{`@media (max-width:640px){.maison-form-row{grid-template-columns:1fr !important}}`}</style>
      {isImported && (
        <div style={{ marginBottom:18, padding:"12px 16px", background:"rgba(74,222,128,0.07)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:10, fontSize:13, color:"#86efac" }}>
          ✨ Fiche importée — vérifie, complète et enregistre.
        </div>
      )}
      <h2 style={{ margin:"0 0 22px", fontSize:20, fontWeight:800, color:"#e2e8f0" }}>
        {isImported ? "📋 Compléter la fiche" : initial?.id && initial.dateAjout ? "✏️ Modifier" : "🏡 Nouvelle maison"}
      </h2>

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:12, marginBottom:12 }}>
        <Field label="Titre / Nom" k="titre" placeholder="Ex: Belle maison Nantes centre" />
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Type</span>
          <select value={m.type} onChange={e => set("type", e.target.value)} style={inp}>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Usage</span>
          <select value={m.usage || "residence"} onChange={e => setM(prev => ({ ...prev, usage: e.target.value, statut: "" }))} style={inp}>
            <option value="residence">Résidence principale</option>
            <option value="investissement">Investissement locatif</option>
            <option value="mixte">Mixte / Indéfini</option>
          </select>
        </label>
      </div>

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12, marginBottom:12 }}>
        <Field label="Adresse" k="adresse" placeholder="12 rue des Lilas" />
        <Field label="Ville" k="ville" placeholder="Nantes" />
      </div>

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
        <Field label="Prix de vente (€)" k="prix" type="number" placeholder="250000" />
        <Field label="Estimation travaux (€)" k="estimationTravaux" type="number" placeholder="30000" />
        <Field label="Agence" k="agence" placeholder="Century 21..." />
      </div>

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:12 }}>
        <Field label="Surface (m²)" k="surface" type="number" />
        <Field label="Terrain (m²)" k="terrain" type="number" />
        <Field label="Nb pièces" k="pieces" type="number" />
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Année constr.</span>
          <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4}
            value={m.anneeConstruction||""} placeholder="ex: 1985"
            onChange={e => { const v = e.target.value.replace(/\D/g,""); set("anneeConstruction", v); }}
            style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:14, outline:"none" }} />
        </label>
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Prix / m² 🔢</span>
          <div style={{ ...inp, opacity:0.7, display:"flex", alignItems:"center", color: prixM2 ? "#a78bfa" : "#4b5563", fontWeight: prixM2 ? 700 : 400 }}>
            {prixM2 ? formatEur(prixM2) : "Auto"}
          </div>
        </label>
      </div>

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>DPE</span>
          <div style={{ display:"flex", gap:5 }}>
            {DPE_OPTIONS.map(d => (
              <button key={d} onClick={() => set("dpe", m.dpe===d ? "" : d)}
                style={{ flex:1, padding:"8px 0", borderRadius:6, border:"2px solid", borderColor: m.dpe===d ? DPE_COLORS[d] : "transparent", background: m.dpe===d ? DPE_COLORS[d]+"33" : "rgba(0,0,0,0.3)", color: m.dpe===d ? DPE_COLORS[d] : "#6b7280", fontWeight:700, cursor:"pointer", fontSize:13 }}>
                {d}
              </button>
            ))}
          </div>
        </label>
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Statut</span>
          <select value={m.statut} onChange={e => set("statut", e.target.value)} style={inp}>
            {getStatutsForUsage(m.usage).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      {m.usage === "investissement" && (
        <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12, padding:"12px 14px", background:"rgba(167,139,250,0.06)", border:"1px solid rgba(167,139,250,0.2)", borderRadius:10 }}>
          <Field label="🎯 Loyer estimé (€/mois)" k="loyerEstime" type="number" placeholder="1200" />
          <Field label="🏘 Loyer moyen secteur (€/m²)" k="loyerM2Secteur" type="number" placeholder="15" />
          <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>📊 Tension locative</span>
            <select value={m.tensionLocative || ""} onChange={e => set("tensionLocative", e.target.value)} style={inp}>
              <option value="">—</option>
              <option value="1">★ Très faible</option>
              <option value="2">★★ Faible</option>
              <option value="3">★★★ Moyenne</option>
              <option value="4">★★★★ Forte</option>
              <option value="5">★★★★★ Très forte</option>
            </select>
          </label>
        </div>
      )}

      <div className="maison-form-row" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
        <label style={{ display:"flex", flexDirection:"column", gap:5 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Date d'ajout</span>
          <div style={{ ...inp, opacity:0.6, fontSize:13, color:"#9ca3af" }}>{m.dateAjout || today()}</div>
        </label>
        <Field label="Date publication annonce" k="datePublication" placeholder="JJ/MM/AAAA" />
        <Field label="Date de visite" k="dateVisite" placeholder="JJ/MM/AAAA" />
      </div>

      <div style={{ marginBottom:12 }}>
        <Field label="Lien vers l'annonce" k="lienAnnonce" placeholder="https://..." />
      </div>

      <div style={{ marginBottom:12 }}>
        <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:8 }}>Photos</span>
        <PhotoUpload photos={m.photos} onChange={fn => setM(prev => ({ ...prev, photos: typeof fn==="function" ? fn(prev.photos) : fn }))} />
      </div>

      {[["Commentaires généraux","commentaires"],["Notes de visite","notesVisite"]].map(([label,k]) => (
        <div key={k} style={{ marginBottom:12 }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:6 }}>{label}</span>
          <textarea value={m[k]||""} onChange={e => set(k,e.target.value)} rows={3}
            style={{ width:"100%", background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:14, outline:"none", resize:"vertical", boxSizing:"border-box" }} />
        </div>
      ))}

      <div style={{ display:"flex", gap:12, justifyContent:"flex-end", marginTop:8 }}>
        <button onClick={onCancel} style={{ padding:"12px 22px", borderRadius:10, border:"1px solid rgba(255,255,255,0.2)", background:"transparent", color:"#9ca3af", cursor:"pointer", fontWeight:600 }}>Annuler</button>
        <button onClick={() => onSave(m)} style={{ padding:"12px 28px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:15 }}>💾 Enregistrer</button>
      </div>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
function MaisonCard({ maison, budget, onClick, onEdit, onDelete, onSimulate, onChecklist, isMobile = false, isSelected = false, onToggleCompare }) {
  const total = Number(maison.prix||0) + Number(maison.estimationTravaux||0);
  const prixM2 = calcPrixM2(maison.prix, maison.surface);
  const isNogo = maison.statut === "No-Go";
  return (
    <div onClick={onClick}
      style={{
        background: isNogo?"rgba(30,30,35,0.5)":"rgba(255,255,255,0.04)",
        border: isSelected ? "1.5px solid #a78bfa" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: isSelected ? "0 0 0 2px rgba(167,139,250,0.18)" : "none",
        borderRadius:16, overflow:"hidden", cursor:"pointer",
        opacity: isNogo?0.5:1,
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}>
      <div style={{ height: isMobile?100:155, background:"rgba(0,0,0,0.3)", position:"relative", overflow:"hidden" }}>
        {maison.photos.length > 0
          ? <img src={maison.photos[0]} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => e.target.style.display="none"} />
          : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:36 }}>🏡</div>}
        {/* Bouton comparer (haut-gauche) */}
        {onToggleCompare && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCompare(); }}
            title={isSelected ? "Retirer du comparateur" : "Ajouter au comparateur"}
            style={{
              position: "absolute", top: 8, left: 8,
              background: isSelected ? "#a78bfa" : "rgba(0,0,0,0.6)",
              border: isSelected ? "1.5px solid #fff" : "1.5px solid rgba(255,255,255,0.3)",
              color: "#fff",
              width: 28, height: 28, borderRadius: "50%",
              cursor: "pointer", fontSize: 13, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
              padding: 0, lineHeight: 1,
            }}
          >
            {isSelected ? "✓" : "⚖"}
          </button>
        )}
        <div style={{ position:"absolute", top:8, right:8, display:"flex", gap:5 }}>
          {maison.dpe && <span style={{ background:DPE_COLORS[maison.dpe], color:"#000", fontWeight:800, fontSize:11, borderRadius:5, padding:"2px 7px" }}>{maison.dpe}</span>}
          <span style={{ background:STATUT_COLORS[maison.statut]||"#6b7280", color:"#fff", fontWeight:700, fontSize:10, borderRadius:5, padding:"2px 7px" }}>{maison.statut}</span>
        </div>
      </div>
      <div style={{ padding:"12px 14px" }}>
        <div style={{ fontWeight:800, fontSize:14, color:"#f1f5f9", marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{maison.titre || `${maison.type} - ${maison.ville}`}</div>
        <div style={{ fontSize:11, color:"#6b7280", marginBottom:8 }}>{maison.ville}{maison.agence ? ` · ${maison.agence}` : ""}</div>
        {maison.listing_status === "removed" && (
          <div style={{ marginBottom:8 }}>
            <ListingStatusBadge maison={maison} />
          </div>
        )}
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
          {[maison.surface&&`${maison.surface}m²`, maison.pieces&&`${maison.pieces}p`, maison.terrain&&`${maison.terrain}m² terrain`].filter(Boolean).map((t,i) => (
            <span key={i} style={{ fontSize:10, background:"rgba(255,255,255,0.07)", borderRadius:5, padding:"2px 7px", color:"#9ca3af" }}>{t}</span>
          ))}
        </div>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:2 }}>
          <span style={{ fontSize:17, fontWeight:800, color:"#a78bfa" }}>{formatEur(maison.prix)}</span>
          {prixM2 && <span style={{ fontSize:11, color:"#6b7280" }}>{formatEur(prixM2)}/m²</span>}
        </div>
        {Number(maison.estimationTravaux)>0 && <div style={{ fontSize:11, color:"#f59e0b", marginBottom:2 }}>+ {formatEur(maison.estimationTravaux)} travaux = {formatEur(total)}</div>}
        {maison.dateVisite && <div style={{ fontSize:11, color:"#60a5fa", marginBottom:4 }}>📅 Visite : {maison.dateVisite}</div>}
        {(() => {
          const { done, total: totalItems } = checklistProgress(maison.visitChecklist);
          if (done === 0) return null;
          return (
            <div onClick={(e) => { e.stopPropagation(); onChecklist?.(maison); }}
              style={{ fontSize:11, color:"#a78bfa", marginBottom:4, cursor:"pointer", display:"inline-block",
                padding:"2px 8px", background:"rgba(167,139,250,0.1)", border:"1px solid rgba(167,139,250,0.3)",
                borderRadius:6, fontWeight:600 }}
              title="Ouvrir la checklist de visite">
              📋 {done}/{totalItems} items
            </div>
          );
        })()}
        <BudgetBar maison={maison} budget={budget} />
        <div style={{ display:"flex", gap:6, marginTop:10 }} onClick={e => e.stopPropagation()}>
          <button onClick={onEdit} style={{ flex:1, padding:"7px", borderRadius:7, border:"1px solid rgba(255,255,255,0.15)", background:"transparent", color:"#9ca3af", cursor:"pointer", fontSize:12 }}>✏️</button>
          {maison.lienAnnonce && <a href={maison.lienAnnonce} target="_blank" rel="noreferrer" style={{ flex:1, padding:"7px", borderRadius:7, border:"1px solid rgba(99,102,241,0.4)", background:"rgba(99,102,241,0.1)", color:"#818cf8", fontSize:12, textAlign:"center", textDecoration:"none" }}>🔗</a>}
          {maison.usage === "investissement" && (
            <button onClick={() => onSimulate?.(maison)} title="Simulation rendement locatif" style={{ flex:1, padding:"7px", borderRadius:7, border:"1px solid rgba(167,139,250,0.4)", background:"rgba(167,139,250,0.1)", color:"#c4b5fd", cursor:"pointer", fontSize:12 }}>📊</button>
          )}
          <button onClick={onDelete} style={{ padding:"7px 10px", borderRadius:7, border:"1px solid rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.07)", color:"#f87171", cursor:"pointer", fontSize:12 }}>🗑</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal détail ──────────────────────────────────────────────────────────────
function DetailModal({ maison, budget, onClose, onEdit, onSimulate, onChecklist, onDocuments }) {
  if (!maison) return null;
  const total = Number(maison.prix||0) + Number(maison.estimationTravaux||0);
  const prixM2 = calcPrixM2(maison.prix, maison.surface);
  const mensualite = calcMensualite(maison.prix, maison.estimationTravaux, budget.apport, budget.tauxInteret, budget.dureeMois);
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#0f0f13", border:"1px solid rgba(255,255,255,0.12)", borderRadius:20, maxWidth:680, width:"100%", maxHeight:"90vh", overflow:"auto" }}>
        <div style={{ height:220, background:"rgba(0,0,0,0.5)", position:"relative", borderRadius:"20px 20px 0 0", overflow:"hidden" }}>
          {maison.photos.length>0 ? <img src={maison.photos[0]} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>e.target.style.display="none"} /> : <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:56 }}>🏡</div>}
          <button onClick={onClose} style={{ position:"absolute", top:12, right:12, background:"rgba(0,0,0,0.6)", border:"none", color:"#fff", width:34, height:34, borderRadius:"50%", fontSize:16, cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:26 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18, gap:10 }}>
            <div>
              <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:"#f1f5f9" }}>{maison.titre || `${maison.type} · ${maison.ville}`}</h2>
              <div style={{ color:"#6b7280", fontSize:12, marginTop:3 }}>
                {[maison.adresse, maison.ville, maison.type, maison.agence && `🏢 ${maison.agence}`].filter(Boolean).join(" · ")}
              </div>
              <div style={{ fontSize:11, color:"#4b5563", marginTop:3 }}>
                {maison.dateAjout && `Ajouté le ${maison.dateAjout}`}
                {maison.datePublication && ` · Publié le ${maison.datePublication}`}
                {maison.dateVisite && ` · Visite : ${maison.dateVisite}`}
              </div>
            </div>
            <span style={{ background:STATUT_COLORS[maison.statut]||"#6b7280", color:"#fff", fontWeight:700, fontSize:11, borderRadius:7, padding:"5px 11px", whiteSpace:"nowrap" }}>{maison.statut}</span>
          </div>

          <div style={{ background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.2)", borderRadius:12, padding:"16px 18px", marginBottom:18 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, textAlign:"center" }}>
              <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>PRIX</div><div style={{ fontSize:17, fontWeight:800, color:"#a78bfa" }}>{formatEur(maison.prix)}</div></div>
              <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>PRIX/M²</div><div style={{ fontSize:17, fontWeight:800, color:"#60a5fa" }}>{prixM2 ? formatEur(prixM2) : "—"}</div></div>
              <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>TRAVAUX</div><div style={{ fontSize:17, fontWeight:800, color:"#f59e0b" }}>{formatEur(maison.estimationTravaux||0)}</div></div>
              <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>TOTAL</div><div style={{ fontSize:17, fontWeight:800, color:"#f1f5f9" }}>{formatEur(total)}</div></div>
            </div>
            {mensualite && <div style={{ marginTop:10, textAlign:"center", fontSize:13, color:"#c4b5fd" }}>~{formatEur(mensualite)}/mois</div>}
            <BudgetBar maison={maison} budget={budget} />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:18 }}>
            {[["Surface",maison.surface?maison.surface+" m²":null],["Terrain",maison.terrain?maison.terrain+" m²":null],["Pièces",maison.pieces],["Année",maison.anneeConstruction],["Agence",maison.agence],["DPE",maison.dpe?<span style={{fontWeight:800,color:DPE_COLORS[maison.dpe]}}>{maison.dpe}</span>:null]].filter(([,v])=>v).map(([label,val]) => (
              <div key={label} style={{ background:"rgba(255,255,255,0.04)", borderRadius:9, padding:"10px 12px" }}>
                <div style={{ fontSize:10, color:"#6b7280", marginBottom:3, textTransform:"uppercase" }}>{label}</div>
                <div style={{ fontWeight:700, color:"#e2e8f0", fontSize:14 }}>{val}</div>
              </div>
            ))}
          </div>

          {maison.photos.length>1 && <div style={{ marginBottom:16 }}><div style={{ fontSize:11, color:"#9ca3af", marginBottom:8, textTransform:"uppercase" }}>Photos</div><div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>{maison.photos.map((p,i)=><img key={i} src={p} alt="" style={{ width:96,height:72,objectFit:"cover",borderRadius:7 }} onError={e=>e.target.style.display="none"} />)}</div></div>}
          {maison.commentaires && <div style={{ marginBottom:14 }}><div style={{ fontSize:11, color:"#9ca3af", marginBottom:6, textTransform:"uppercase" }}>Commentaires</div><div style={{ background:"rgba(255,255,255,0.04)", borderRadius:9, padding:13, color:"#d1d5db", fontSize:13, lineHeight:1.6 }}>{maison.commentaires}</div></div>}
          {maison.notesVisite && <div style={{ marginBottom:14 }}><div style={{ fontSize:11, color:"#9ca3af", marginBottom:6, textTransform:"uppercase" }}>Notes de visite</div><div style={{ background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:9, padding:13, color:"#fcd34d", fontSize:13, lineHeight:1.6 }}>{maison.notesVisite}</div></div>}

          {maison.usage === "investissement" && (
            <div style={{ marginTop:10 }}>
              <button onClick={() => onSimulate?.(maison)} style={{
                width:"100%", padding:"12px 16px", borderRadius:10,
                background:"linear-gradient(135deg, rgba(167,139,250,0.18), rgba(96,165,250,0.18))",
                border:"1px solid rgba(167,139,250,0.4)", color:"#c4b5fd",
                fontWeight:700, fontSize:14, cursor:"pointer"
              }}>
                💰 Simulation rendement locatif
              </button>
            </div>
          )}
          <div style={{ marginTop:10 }}>
            {(() => {
              const { done, total: totalItems } = checklistProgress(maison.visitChecklist);
              const hasProgress = done > 0;
              return (
                <button onClick={() => onChecklist?.(maison)} style={{
                  width:"100%", padding:"12px 16px", borderRadius:10,
                  background: hasProgress ? "linear-gradient(135deg, rgba(167,139,250,0.18), rgba(34,197,94,0.12))" : "rgba(255,255,255,0.05)",
                  border:"1px solid rgba(167,139,250,0.4)", color:"#c4b5fd",
                  fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8
                }}>
                  📋 Checklist de visite
                  {hasProgress && <span style={{ fontSize:12, fontWeight:600, opacity:0.85 }}>· {done}/{totalItems}</span>}
                </button>
              );
            })()}
          </div>
          <div style={{ marginTop:10 }}>
            {(() => {
              const docCount = Array.isArray(maison.documents) ? maison.documents.length : 0;
              return (
                <button onClick={() => onDocuments?.(maison)} style={{
                  width:"100%", padding:"12px 16px", borderRadius:10,
                  background: docCount > 0 ? "linear-gradient(135deg, rgba(96,165,250,0.18), rgba(167,139,250,0.12))" : "rgba(255,255,255,0.05)",
                  border:"1px solid rgba(96,165,250,0.4)", color:"#93c5fd",
                  fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8
                }}>
                  📎 Documents
                  {docCount > 0 && <span style={{ fontSize:12, fontWeight:600, opacity:0.85 }}>· {docCount}</span>}
                </button>
              );
            })()}
          </div>

          <div style={{ display:"flex", gap:10, marginTop:18 }}>
            <button onClick={onEdit} style={{ flex:1, padding:"11px", borderRadius:9, border:"1px solid rgba(255,255,255,0.2)", background:"transparent", color:"#9ca3af", cursor:"pointer", fontWeight:600 }}>✏️ Modifier</button>
            {maison.lienAnnonce && <a href={maison.lienAnnonce} target="_blank" rel="noreferrer" style={{ flex:1, padding:"11px", borderRadius:9, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, textAlign:"center", textDecoration:"none" }}>🔗 Voir l'annonce</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Auth Modal ───────────────────────────────────────────────────────────────
function AuthModal({ onClose, onShowPrivacy }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // "email" | "code"
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false);
  const inp = { background:"rgba(0,0,0,0.4)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:10, padding:"13px 14px", color:"#fff", fontSize:15, outline:"none", width:"100%", boxSizing:"border-box" };

  const handleSend = async () => {
    if (!email.includes("@")) { setError("Entre une adresse email valide"); return; }
    if (!consent) { setError("Tu dois accepter la politique de confidentialité"); return; }
    setStatus("loading"); setError("");
    if (!supabase) { setError("Supabase non configuré"); setStatus("error"); return; }
    const { error: e } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        data: { consent_accepted_at: new Date().toISOString() },
      },
    });
    if (e) { setError(e.message); setStatus("error"); }
    else { setStep("code"); setStatus("idle"); }
  };

  const handleVerify = async () => {
    const token = code.trim();
    if (token.length < 6) { setError("Entre le code reçu par email"); return; }
    setStatus("loading"); setError("");
    const { error: e } = await supabase.auth.verifyOtp({ email, token, type: "email" });
    if (e) { setError("Code invalide ou expiré. Réessaie."); setStatus("error"); }
    else { onClose(); } // la session est établie, onAuthStateChange prend le relais
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#0f0f13", border:"1px solid rgba(255,255,255,0.12)", borderRadius:20, maxWidth:400, width:"100%", padding:32 }}>
        <h2 style={{ margin:"0 0 8px", fontSize:20, fontWeight:800, color:"#f1f5f9" }}>🔐 Connexion</h2>

        {step === "email" ? (
          <>
            <p style={{ margin:"0 0 24px", fontSize:13, color:"#6b7280", lineHeight:1.6 }}>
              Entre ton email — on t'envoie un code de connexion. Tes annonces seront synchronisées sur tous tes appareils.
            </p>
            <input value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter" && handleSend()}
              placeholder="ton@email.com" type="email" autoComplete="email" style={inp} />

            <label style={{ display:"flex", alignItems:"flex-start", gap:9, marginTop:14, cursor:"pointer" }}>
              <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} style={{ marginTop:2, width:16, height:16, accentColor:"#a78bfa", flexShrink:0, cursor:"pointer" }} />
              <span style={{ fontSize:12, color:"#9ca3af", lineHeight:1.5 }}>
                J'accepte la{" "}
                <button onClick={(ev)=>{ev.preventDefault(); onShowPrivacy?.();}} style={{ background:"none", border:"none", color:"#a78bfa", textDecoration:"underline", cursor:"pointer", padding:0, fontSize:12 }}>
                  politique de confidentialité
                </button>
                .
              </span>
            </label>

            {error && <div style={{ color:"#f87171", fontSize:13, marginTop:8 }}>⚠️ {error}</div>}
            <button onClick={handleSend} disabled={status==="loading"}
              style={{ width:"100%", marginTop:14, padding:"13px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:15, opacity:status==="loading"?0.6:1 }}>
              {status==="loading" ? "Envoi…" : "✉️ Recevoir mon code"}
            </button>
            <button onClick={onClose} style={{ width:"100%", marginTop:8, padding:"11px", borderRadius:10, border:"1px solid rgba(255,255,255,0.1)", background:"transparent", color:"#6b7280", cursor:"pointer", fontSize:14 }}>
              Continuer sans compte
            </button>
          </>
        ) : (
          <>
            <p style={{ margin:"0 0 20px", fontSize:13, color:"#6b7280", lineHeight:1.6 }}>
              On a envoyé un code de connexion à <b style={{ color:"#d1d5db" }}>{email}</b>. Saisis-le ci-dessous (pense à vérifier les spams).
            </p>
            <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,"").slice(0,8))} onKeyDown={e=>e.key==="Enter" && handleVerify()}
              placeholder="••••••" inputMode="numeric" autoComplete="one-time-code" maxLength={8}
              style={{ ...inp, textAlign:"center", fontSize:22, letterSpacing:"0.3em", fontWeight:700 }} autoFocus />
            {error && <div style={{ color:"#f87171", fontSize:13, marginTop:8 }}>⚠️ {error}</div>}
            <button onClick={handleVerify} disabled={status==="loading"}
              style={{ width:"100%", marginTop:14, padding:"13px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:15, opacity:status==="loading"?0.6:1 }}>
              {status==="loading" ? "Vérification…" : "Se connecter"}
            </button>
            <button onClick={()=>{ setStep("email"); setCode(""); setError(""); setStatus("idle"); }} style={{ width:"100%", marginTop:8, padding:"11px", borderRadius:10, border:"1px solid rgba(255,255,255,0.1)", background:"transparent", color:"#6b7280", cursor:"pointer", fontSize:14 }}>
              ← Changer d'email
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ComparatorView ───────────────────────────────────────────────────────────
function ComparatorView({ maisons, ids, budgets, onClose, onRemove, onShowDetail }) {
  const isMobile = useMobile();
  const items = ids.map(id => maisons.find(m => m.id === id)).filter(Boolean);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (items.length === 0) return null;

  const dpeRank = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7 };
  const minIdx = (arr) => {
    const valid = arr.map((v, i) => v != null && v !== "" && Number.isFinite(Number(v)) ? [Number(v), i] : null).filter(Boolean);
    if (valid.length < 2) return -1;
    return valid.reduce((best, cur) => cur[0] < best[0] ? cur : best)[1];
  };
  const maxIdx = (arr) => {
    const valid = arr.map((v, i) => v != null && v !== "" && Number.isFinite(Number(v)) ? [Number(v), i] : null).filter(Boolean);
    if (valid.length < 2) return -1;
    return valid.reduce((best, cur) => cur[0] > best[0] ? cur : best)[1];
  };

  const prices       = items.map(m => Number(m.prix) || null);
  const surfaces     = items.map(m => Number(m.surface) || null);
  const pricesM2     = items.map(m => calcPrixM2(m.prix, m.surface));
  const terrains     = items.map(m => Number(m.terrain) || null);
  const piecesArr    = items.map(m => Number(m.pieces) || null);
  const annees       = items.map(m => Number(m.anneeConstruction) || null);
  const dpes         = items.map(m => m.dpe ? dpeRank[m.dpe] : null);
  const travaux      = items.map(m => Number(m.estimationTravaux) || 0);
  const fraisNotaires= items.map(m => Math.round((Number(m.prix) || 0) * 0.08));
  const totaux       = items.map((m, i) => (Number(m.prix) || 0) + travaux[i] + fraisNotaires[i]);
  const mensualites  = items.map(m => {
    const b = getBudgetForMaison(budgets, m);
    return calcMensualite(m.prix, m.estimationTravaux, b.apport, b.tauxInteret, b.dureeMois);
  });

  const best = {
    prix:        minIdx(prices),
    prixM2:      minIdx(pricesM2),
    surface:     maxIdx(surfaces),
    terrain:     maxIdx(terrains),
    pieces:      maxIdx(piecesArr),
    annee:       maxIdx(annees),
    dpe:         minIdx(dpes),
    travaux:     minIdx(travaux.map(t => t > 0 ? t : null)),
    total:       minIdx(totaux.map(t => t > 0 ? t : null)),
    mensualite:  minIdx(mensualites),
  };

  const labelCol = {
    padding: "11px 14px",
    fontSize: 11, color: "#9ca3af", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.05em",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    position: "sticky", left: 0, zIndex: 2,
    background: "#0f0f13",
    whiteSpace: "nowrap",
    display: "flex", alignItems: "center",
  };
  const valueCol = (isBest) => ({
    padding: "11px 14px",
    fontSize: 13, color: isBest ? "#86efac" : "#e2e8f0",
    fontWeight: isBest ? 700 : 500,
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    borderLeft: "1px solid rgba(255,255,255,0.05)",
    background: isBest ? "rgba(74,222,128,0.07)" : "transparent",
    display: "flex", alignItems: "center",
    minWidth: 0,
  });
  const sectionRow = {
    gridColumn: "1 / -1",
    padding: "14px 14px 6px",
    fontSize: 10, color: "#a78bfa", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.1em",
    background: "rgba(167,139,250,0.04)",
    borderTop: "1px solid rgba(167,139,250,0.12)",
  };

  const Best = () => <span style={{ marginRight: 5, fontSize: 11 }}>🏆</span>;
  const Dash = () => <span style={{ color: "#4b5563" }}>—</span>;

  const Row = ({ label, sub, render }) => (
    <>
      <div style={labelCol}>
        <div>
          {label}
          {sub && <div style={{ fontSize: 9, color: "#6b7280", textTransform: "none", letterSpacing: 0, marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      {items.map((m, i) => (
        <div key={m.id} style={valueCol(false)}>
          {render(m, i)}
        </div>
      ))}
    </>
  );

  const NumRow = ({ label, sub, values, bestI, format = formatEur, suffix = "" }) => (
    <>
      <div style={labelCol}>
        <div>
          {label}
          {sub && <div style={{ fontSize: 9, color: "#6b7280", textTransform: "none", letterSpacing: 0, marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      {values.map((v, i) => (
        <div key={items[i].id} style={valueCol(i === bestI)}>
          {i === bestI && <Best />}
          {v == null || v === "" || (typeof v === "number" && !Number.isFinite(v)) ? <Dash /> : <span>{format(v)}{suffix}</span>}
        </div>
      ))}
    </>
  );

  const cols = `130px repeat(${items.length}, minmax(170px, 1fr))`;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      zIndex: 2100, display: "flex",
      alignItems: isMobile ? "flex-end" : "center",
      justifyContent: "center",
      padding: isMobile ? 0 : 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0f0f13",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: isMobile ? "20px 20px 0 0" : 20,
        width: "100%",
        maxWidth: 1100,
        maxHeight: isMobile ? "94vh" : "90vh",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        <div style={{
          padding: "18px 22px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>
              ⚖️ Comparateur ({items.length})
            </h2>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
              🏆 = meilleure valeur sur la ligne
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.06)", border: "none", color: "#9ca3af",
            width: 34, height: 34, borderRadius: "50%", fontSize: 18, cursor: "pointer",
          }}>×</button>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: cols,
            minWidth: "fit-content",
          }}>
            <div style={sectionRow}>📋 Identité</div>

            <div style={labelCol}>Photo</div>
            {items.map(m => (
              <div key={m.id} style={{ ...valueCol(false), padding: "10px 12px" }}>
                <div style={{
                  width: "100%", aspectRatio: "16/10", borderRadius: 8,
                  background: "rgba(0,0,0,0.4)", overflow: "hidden",
                  position: "relative",
                }}>
                  {m.photos?.[0]
                    ? <img src={m.photos[0]} alt="" onError={e => e.target.style.display = "none"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🏡</div>
                  }
                </div>
              </div>
            ))}

            <Row label="Titre" render={(m) => (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", width: "100%" }}>
                {m.titre || `${m.type} · ${m.ville || "—"}`}
              </span>
            )} />

            <Row label="Ville" render={(m) => m.ville || <Dash />} />

            <Row label="Statut" render={(m) => (
              <span style={{
                background: STATUT_COLORS[m.statut] + "22",
                color: STATUT_COLORS[m.statut],
                fontWeight: 700, fontSize: 11,
                padding: "3px 8px", borderRadius: 6,
                border: `1px solid ${STATUT_COLORS[m.statut]}44`,
              }}>{m.statut}</span>
            )} />

            <div style={sectionRow}>💰 Prix</div>

            <NumRow label="Prix de vente" values={prices} bestI={best.prix} />
            <NumRow label="Prix / m²" values={pricesM2} bestI={best.prixM2} />
            <NumRow label="Travaux estimés" values={travaux.map(t => t > 0 ? t : null)} bestI={best.travaux} />
            <NumRow label="Frais notaire" sub="~8%" values={fraisNotaires.map(f => f > 0 ? f : null)} bestI={-1} />
            <NumRow label="Coût total" sub="prix + travaux + notaire" values={totaux.map(t => t > 0 ? t : null)} bestI={best.total} />
            <NumRow label="Mensualité" sub="estimée /mois" values={mensualites} bestI={best.mensualite} />

            <div style={sectionRow}>🏠 Caractéristiques</div>

            <NumRow label="Surface" values={surfaces} bestI={best.surface} format={(v) => `${v} m²`} />
            <NumRow label="Terrain" values={terrains} bestI={best.terrain} format={(v) => `${v} m²`} />
            <NumRow label="Pièces" values={piecesArr} bestI={best.pieces} format={(v) => v} />
            <NumRow label="Année construction" values={annees} bestI={best.annee} format={(v) => v} />

            <div style={labelCol}>DPE</div>
            {items.map((m, i) => (
              <div key={m.id} style={valueCol(i === best.dpe)}>
                {i === best.dpe && <Best />}
                {m.dpe
                  ? <span style={{
                      background: DPE_COLORS[m.dpe], color: "#000",
                      fontWeight: 800, fontSize: 12,
                      padding: "3px 10px", borderRadius: 5,
                    }}>{m.dpe}</span>
                  : <Dash />}
              </div>
            ))}

            <div style={sectionRow}>📅 Dates</div>

            <Row label="Date d'ajout" render={(m) => m.dateAjout || <Dash />} />
            <Row label="Mise en vente" render={(m) => m.datePublication || <Dash />} />
            <Row label="Visite" render={(m) => m.dateVisite
              ? <span style={{ color: "#60a5fa" }}>{m.dateVisite}</span>
              : <Dash />} />

            <div style={sectionRow}>↗ Actions</div>

            <div style={labelCol}></div>
            {items.map(m => (
              <div key={m.id} style={{ ...valueCol(false), flexDirection: "column", alignItems: "stretch", gap: 6, padding: "12px 14px" }}>
                <button onClick={() => onShowDetail(m)} style={{
                  padding: "8px", borderRadius: 7, border: "none",
                  background: "linear-gradient(135deg,#7c3aed,#a78bfa)",
                  color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12,
                }}>Voir la fiche</button>
                {m.lienAnnonce && (
                  <a href={m.lienAnnonce} target="_blank" rel="noreferrer" style={{
                    padding: "7px", borderRadius: 7, border: "1px solid rgba(99,102,241,0.4)",
                    background: "rgba(99,102,241,0.1)", color: "#818cf8",
                    fontSize: 11, textAlign: "center", textDecoration: "none",
                  }}>🔗 Annonce</a>
                )}
                <button onClick={() => onRemove(m.id)} style={{
                  padding: "7px", borderRadius: 7,
                  border: "1px solid rgba(239,68,68,0.3)",
                  background: "rgba(239,68,68,0.07)",
                  color: "#f87171", cursor: "pointer", fontSize: 11,
                }}>Retirer</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ComparisonBar ────────────────────────────────────────────────────────────
function ComparisonBar({ maisons, ids, isMobile, bottomOffset, onRemove, onClear, onCompare }) {
  if (ids.length === 0) return null;
  const items = ids.map(id => maisons.find(m => m.id === id)).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: isMobile ? bottomOffset + 10 : 18,
      left: 12, right: 12,
      zIndex: 1100,
      maxWidth: 600,
      margin: "0 auto",
      background: "rgba(15,15,19,0.97)",
      border: "1px solid rgba(167,139,250,0.4)",
      borderRadius: 14,
      padding: "10px 12px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
      display: "flex",
      gap: 10,
      alignItems: "center",
      backdropFilter: "blur(10px)",
    }}>
      <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 0, overflow: "hidden" }}>
        {items.map(m => (
          <div key={m.id} onClick={() => onRemove(m.id)} title="Retirer du comparateur" style={{
            position: "relative",
            width: 40, height: 40,
            borderRadius: 8,
            background: "rgba(0,0,0,0.4)",
            overflow: "hidden",
            cursor: "pointer",
            flexShrink: 0,
            border: "1.5px solid rgba(167,139,250,0.5)",
          }}>
            {m.photos?.[0]
              ? <img src={m.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => e.target.style.display = "none"} />
              : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", fontSize: 18 }}>🏡</div>
            }
          </div>
        ))}
        {Array.from({ length: 3 - items.length }).map((_, i) => (
          <div key={`empty-${i}`} style={{
            width: 40, height: 40,
            borderRadius: 8,
            border: "1.5px dashed rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#4b5563", fontSize: 16,
            flexShrink: 0,
          }}>+</div>
        ))}
      </div>

      {items.length < 2 ? (
        <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>
          + {2 - items.length} pour comparer
        </span>
      ) : (
        <button onClick={onCompare} style={{
          padding: "9px 14px", borderRadius: 9, border: "none",
          background: "linear-gradient(135deg,#7c3aed,#a78bfa)",
          color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13,
          whiteSpace: "nowrap",
        }}>
          ⚖️ Comparer
        </button>
      )}

      <button onClick={onClear} title="Vider la sélection" style={{
        background: "rgba(255,255,255,0.06)", border: "none", color: "#9ca3af",
        width: 28, height: 28, borderRadius: "50%", fontSize: 14,
        cursor: "pointer", flexShrink: 0,
      }}>×</button>
    </div>
  );
}

// ── App principale ────────────────────────────────────────────────────────────
export default function App() {
  const isMobile = useMobile();

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [maisons, setMaisonsRaw] = useState(() => { try { return JSON.parse(localStorage.getItem("maisons")||"[]"); } catch { return []; } });
  const [budgets, setBudgetsRaw] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("budget") || "null");
      // Migration : ancien format (champs à plat) → nouveau format (rp/loc)
      if (raw && raw.rp && raw.loc) return raw;
      if (raw && (raw.prixAchat || raw.apport || raw.tauxInteret || raw.dureeMois)) {
        return { rp: { ...defaultBudgets.rp, ...raw }, loc: defaultBudgets.loc };
      }
      return defaultBudgets;
    } catch { return defaultBudgets; }
  });

  const [view, setView] = useState("grid");
  const [mobileTab, setMobileTab] = useState("annonces");
  const [editingMaison, setEditingMaison] = useState(null);
  const [detailMaison, setDetailMaison] = useState(null);
  const [simulatingMaison, setSimulatingMaison] = useState(null);
  const [checklistMaison, setChecklistMaison] = useState(null);
  const [documentsMaison, setDocumentsMaison] = useState(null);
  const [sortBy, setSortBy] = useState("dateAjout");
  const [filterStatut, setFilterStatut] = useState("Tous");
  const [filterUsage, setFilterUsage] = useState(() => localStorage.getItem("filterUsage") || "all");
  const [searchQ, setSearchQ] = useState("");
  const [showBudget, setShowBudget] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [showCarte, setShowCarte] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [comparison, setComparisonRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem("comparison") || "[]"); }
    catch { return []; }
  });
  const [showComparator, setShowComparator] = useState(false);

  const setComparison = useCallback((updater) => {
    setComparisonRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("comparison", JSON.stringify(next));
      return next;
    });
  }, []);

  const toggleCompare = useCallback((id) => {
    setComparison(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) {
        window.alert("Maximum 3 maisons à comparer. Retire-en une avant d'en ajouter une nouvelle.");
        return prev;
      }
      return [...prev, id];
    });
  }, [setComparison]);

  const loadFromSupabase = useCallback(async (uid) => {
    if (!supabase) return;
    setSyncing(true);
    try {
      const [{ data: rows }, { data: bRow }] = await Promise.all([
        supabase.from("maisons").select("id,data").eq("user_id", uid).order("created_at", { ascending: false }),
        supabase.from("budgets").select("data").eq("user_id", uid).maybeSingle(),
      ]);
      if (rows) {
        const loaded = rows.map(r => ({ ...r.data, id: r.id }));
        setMaisonsRaw(loaded);
        localStorage.setItem("maisons", JSON.stringify(loaded));
      }
      if (bRow?.data) {
        const raw = bRow.data;
        let migrated;
        if (raw.rp && raw.loc) migrated = raw;
        else if (raw.prixAchat || raw.apport || raw.tauxInteret || raw.dureeMois) {
          migrated = { rp: { ...defaultBudgets.rp, ...raw }, loc: defaultBudgets.loc };
        } else migrated = defaultBudgets;
        setBudgetsRaw(migrated);
        localStorage.setItem("budget", JSON.stringify(migrated));
      }
    } catch(e) { console.error("Supabase load error", e); }
    setSyncing(false);
  }, []);

  useEffect(() => {
    if (!isConfigured() || !supabase) { setAuthReady(true); return; }
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthReady(true);
      if (session?.user) loadFromSupabase(session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((authEvent, session) => {
      setUser(session?.user ?? null);
      if (authEvent === "SIGNED_IN") track("signup");
      if (session?.user) loadFromSupabase(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, [loadFromSupabase]);

  const setMaisons = useCallback(async (updater) => {
    setMaisonsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("maisons", JSON.stringify(next));
      return next;
    });
  }, []);

  const setBudgets = useCallback(async (updater) => {
    setBudgetsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("budget", JSON.stringify(next));
      if (user && supabase) {
        supabase.from("budgets").upsert({ user_id: user.id, data: next, updated_at: new Date().toISOString() }).then(() => {});
      }
      return next;
    });
  }, [user]);

  const saveMaisonToSupabase = useCallback(async (m, uid) => {
    if (!uid || !supabase) return;
    await supabase.from("maisons").upsert({
      id: m.id, user_id: uid, data: m, updated_at: new Date().toISOString()
    });
  }, []);

  const deleteMaisonFromSupabase = useCallback(async (id, uid) => {
    if (!uid || !supabase) return;
    await supabase.from("maisons").delete().eq("id", id).eq("user_id", uid);
  }, []);

  useEffect(() => {
    const handleUrl = () => {
      const data = getImportFromUrl();
      if (data) {
        window.history.replaceState({}, "", window.location.pathname);
        setEditingMaison(buildMaisonFromData(data));
        setView("form");
        return;
      }
      const shared = getSharedUrl();
      if (shared) {
        window.history.replaceState({}, "", window.location.pathname);
        setShareUrl(shared);
        setShowImport(true);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      if (params.get("action") === "import") {
        window.history.replaceState({}, "", window.location.pathname);
        setShowImport(true);
      }
    };
    handleUrl();

    const onSwMessage = (event) => {
      if (event.data?.type === "share-target") {
        const raw = (event.data.url || "") + " " + (event.data.text || "");
        const match = raw.match(/https?:\/\/\S+/);
        if (match) {
          setShareUrl(match[0]);
          setShowImport(true);
        }
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onSwMessage);
  }, []);

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => setInstallable(false);
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => { window.removeEventListener("pwa-installable", onInstallable); window.removeEventListener("pwa-installed", onInstalled); };
  }, []);

  // Capture globale des erreurs non gérées (async + hors React) → table error_logs
  useEffect(() => {
    const onError = (event) => {
      logError(event.error || new Error(event.message || "window error"), { type: "window.onerror" });
    };
    const onRejection = (event) => {
      logError(event.reason || new Error("Unhandled promise rejection"), { type: "unhandledrejection" });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  const saveMaison = async (m) => {
    let toSave = m;
    if ((m.adresse || m.ville) && (m.lat == null || m.lng == null)) {
      const key = [m.adresse, m.ville].filter(Boolean).join(', ');
      const coords = await geocodeAddress(key);
      if (coords) toSave = { ...m, lat: coords.lat, lng: coords.lng };
    }
    setMaisons(prev => {
      const exists = prev.find(x => x.id === toSave.id);
      if (!exists) {
        track("bien_ajoute");
        if (prev.length === 1) track("comparaison_creee");
      }
      return exists ? prev.map(x => x.id===toSave.id ? toSave : x) : [...prev, toSave];
    });
    if (user) await saveMaisonToSupabase(toSave, user.id);
    setView("grid");
    setEditingMaison(null);
  };

  // Handler pour BatchVerifyButton : met à jour state + Supabase sans changer de vue
  const handleMaisonVerified = useCallback(async (updated) => {
    setMaisons(prev => prev.map(x => x.id === updated.id ? updated : x));
    if (user) await saveMaisonToSupabase(updated, user.id);
  }, [user, setMaisons, saveMaisonToSupabase]);

  // Handler pour MapView : persiste les coordonnées trouvées lors du géocodage à la volée
  const handleMaisonGeocoded = useCallback(async (updated) => {
    setMaisons(prev => prev.map(x => x.id === updated.id ? updated : x));
    if (user) await saveMaisonToSupabase(updated, user.id);
  }, [user, setMaisons, saveMaisonToSupabase]);

  const handleImportDone = maison => {
    setShowImport(false);
    setShareUrl(null);
    setEditingMaison(maison);
    setView("form");
  };

  const shareComparaison = async () => {
    const text = `Je compare ${maisons.length} biens sur HomeFinder : ${maisons.slice(0,5).map(m=>m.titre||m.ville).filter(Boolean).join(", ")}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Mon comparatif HomeFinder", text });
      } else {
        await navigator.clipboard.writeText(text);
        alert("Résumé du comparatif copié dans le presse-papiers !");
      }
      track("comparaison_partagee");
    } catch (e) {
      // partage annulé par l'utilisateur : pas de tracking
    }
  };

  const deleteMaison = async (id) => {
    if (!window.confirm("Supprimer cette maison ?")) return;
    const target = maisons.find(m => m.id === id);
    // Nettoyage des documents Supabase Storage attachés à ce bien
    if (target && Array.isArray(target.documents) && target.documents.length > 0) {
      const paths = target.documents.map(d => d.path).filter(Boolean);
      if (paths.length > 0) {
        try { await supabase.storage.from("property-documents").remove(paths); }
        catch (e) { console.warn("[deleteMaison] storage cleanup failed:", e); }
      }
    }
    setMaisons(prev => prev.filter(m => m.id!==id));
    setComparison(prev => prev.filter(x => x !== id));
    if (user) await deleteMaisonFromSupabase(id, user.id);
  };

  const filtered = maisons
    .filter(m => filterUsage === "all" || (m.usage || "residence") === filterUsage)
    .filter(m => filterStatut==="Tous" || m.statut===filterStatut)
    .filter(m => !searchQ || [m.titre,m.ville,m.adresse,m.agence].join(" ").toLowerCase().includes(searchQ.toLowerCase()))
    .sort((a,b) => {
      if (sortBy==="prix") return Number(a.prix||0)-Number(b.prix||0);
      if (sortBy==="surface") return Number(b.surface||0)-Number(a.surface||0);
      if (sortBy==="prixm2") return (calcPrixM2(a.prix,a.surface)||0)-(calcPrixM2(b.prix,b.surface)||0);
      if (sortBy==="ville") return (a.ville||"").localeCompare(b.ville||"");
      return (b.id||0)-(a.id||0);
    });

  const stats = {
    total: maisons.length,
    coups: maisons.filter(m=>m.statut==="Coup de cœur").length,
    visitees: maisons.filter(m=>["Visitée","Visite planifiée"].includes(m.statut)).length,
    prixMoyen: maisons.length ? Math.round(maisons.reduce((s,m)=>s+Number(m.prix||0),0)/maisons.length) : 0,
  };

  if (view==="form") return (
    <div style={{ minHeight:"100vh", background:"#0a0a0f", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ maxWidth:780, margin:"0 auto", padding: isMobile ? "16px 12px 90px" : "20px 16px" }}>
        <MaisonForm initial={editingMaison} onSave={saveMaison} onCancel={() => { setView("grid"); setEditingMaison(null); }} />
      </div>
    </div>
  );

  const BOTTOM_NAV_H = 64;

  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0f", fontFamily:"'DM Sans',sans-serif", color:"#f1f5f9", paddingBottom: isMobile ? BOTTOM_NAV_H + 16 : 0 }}>

      <div style={{ background:"#0d0d12", borderBottom:"1px solid rgba(255,255,255,0.07)", padding: isMobile ? "14px 16px" : "18px 24px", position:"sticky", top:0, zIndex:50, transform:"translateZ(0)" }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <h1 style={{ margin:0, fontSize: isMobile?19:22, fontWeight:800, background:"linear-gradient(135deg,#a78bfa,#818cf8)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>🏡 HomeFinder</h1>
          {!isMobile && (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {syncing && <span style={{ fontSize:11, color:"#6b7280", animation:"pulse 1.5s infinite" }}>⟳ sync…</span>}
              {isConfigured() && (
                user
                  ? <button onClick={() => setShowAccount(true)} title={user.email} style={{ padding:"7px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.04)", color:"#9ca3af", cursor:"pointer", fontSize:12, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      👤 {user.email.split("@")[0]} · Déconnexion
                    </button>
                  : <button onClick={() => setShowAuth(true)} style={{ padding:"7px 14px", borderRadius:8, border:"1px solid rgba(167,139,250,0.4)", background:"rgba(167,139,250,0.08)", color:"#a78bfa", cursor:"pointer", fontWeight:600, fontSize:12 }}>
                      🔐 Connexion / Sync
                    </button>
              )}
              <button onClick={() => setShowCarte(s=>!s)} style={{ padding:"9px 14px", borderRadius:9, border:`1px solid ${showCarte?"rgba(96,165,250,0.5)":"rgba(96,165,250,0.25)"}`, background: showCarte?"rgba(96,165,250,0.12)":"rgba(96,165,250,0.05)", color:"#60a5fa", cursor:"pointer", fontWeight:600, fontSize:13 }}>🗺️ Carte</button>
              <button onClick={() => setShowBudget(b=>!b)} style={{ padding:"9px 14px", borderRadius:9, border:"1px solid rgba(167,139,250,0.3)", background: showBudget?"rgba(167,139,250,0.15)":"rgba(167,139,250,0.06)", color:"#a78bfa", cursor:"pointer", fontWeight:600, fontSize:13 }}>💰 Budget</button>
              <button onClick={() => setShowImport(true)} style={{ padding:"9px 16px", borderRadius:9, border:"1px solid rgba(99,102,241,0.4)", background:"rgba(99,102,241,0.1)", color:"#818cf8", cursor:"pointer", fontWeight:700, fontSize:13 }}>✨ Importer</button>
              <button onClick={() => { setEditingMaison(null); setView("form"); }} style={{ padding:"9px 18px", borderRadius:9, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:13 }}>+ Manuel</button>
            </div>
          )}
          {isMobile && syncing && (
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <span style={{ fontSize:11, color:"#6b7280" }}>⟳ sync…</span>
            </div>
          )}
        </div>
      </div>

      {installable && (
        <div style={{ background:"linear-gradient(135deg,rgba(124,58,237,0.15),rgba(99,102,241,0.15))", borderBottom:"1px solid rgba(124,58,237,0.3)", padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div style={{ fontSize:13, color:"#c4b5fd" }}>📲 Installe HomeFinder sur ton téléphone !</div>
          <button onClick={() => window.installPWA()} style={{ padding:"7px 14px", borderRadius:8, border:"none", background:"#7c3aed", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>
            Installer
          </button>
        </div>
      )}

      <div style={{ maxWidth:1200, margin:"0 auto", padding: isMobile ? "14px 12px" : "22px 16px" }}>

        {!user && maisons.length === 0 && (
          <div style={{ textAlign:"center", padding: isMobile ? "36px 20px" : "56px 32px", marginBottom: isMobile?14:22, background:"linear-gradient(135deg,rgba(124,58,237,0.1),rgba(99,102,241,0.08))", border:"1px solid rgba(124,58,237,0.25)", borderRadius:18 }}>
            <h2 style={{ margin:"0 0 10px", fontSize: isMobile?21:28, fontWeight:800, color:"#f1f5f9", lineHeight:1.3 }}>
              Compare tes biens immobiliers en un coup d'œil
            </h2>
            <p style={{ margin: isMobile ? "0 0 20px" : "0 0 26px", fontSize: isMobile?14:16, color:"#9ca3af", maxWidth:480, marginLeft:"auto", marginRight:"auto" }}>
              Centralise tes annonces, ton budget et tes visites — et compare tes biens côte à côte pour choisir en toute confiance.
            </p>
            <button onClick={() => setShowAuth(true)} style={{ padding: isMobile?"12px 22px":"14px 28px", borderRadius:11, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize: isMobile?14:15 }}>
              Créer mon comparateur (gratuit)
            </button>
          </div>
        )}

        {isMobile ? (
          <div style={{ display:"flex", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:11, marginBottom:10, overflow:"hidden" }}>
            {[{label:"Biens",val:stats.total,icon:"🏠"},{label:"Coups ♥",val:stats.coups,icon:"❤️"},{label:"Visitées",val:stats.visitees,icon:"📅"},{label:"Prix moy.",val:formatEur(stats.prixMoyen),icon:"💶"}].map((s,i) => (
              <div key={s.label} style={{ flex:1, minWidth:0, textAlign:"center", padding:"7px 3px", borderLeft: i>0 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#f1f5f9", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.icon} {s.val}</div>
                <div style={{ fontSize:8, color:"#6b7280", marginTop:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:22 }}>
            {[{label:"Annonces",val:stats.total,icon:"🏠"},{label:"Coups de cœur",val:stats.coups,icon:"❤️"},{label:"Visitées",val:stats.visitees,icon:"📅"},{label:"Prix moyen",val:formatEur(stats.prixMoyen),icon:"💶"}].map(s => (
              <div key={s.label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:11, padding:"14px 16px" }}>
                <div style={{ fontSize:16 }}>{s.icon}</div>
                <div style={{ fontSize:20, fontWeight:800, color:"#f1f5f9", marginTop:4 }}>{s.val}</div>
                <div style={{ fontSize:10, color:"#6b7280", marginTop:1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {!isMobile && maisons.length >= 2 && (
          <button onClick={shareComparaison} style={{ marginBottom:22, padding:"9px 16px", borderRadius:9, border:"1px solid rgba(167,139,250,0.3)", background:"rgba(167,139,250,0.06)", color:"#a78bfa", cursor:"pointer", fontWeight:600, fontSize:13 }}>
            📤 Partager mon comparatif
          </button>
        )}

        {(showCarte || mobileTab === "carte") ? (
          <MapView maisons={filtered.filter(m=>m.adresse||m.ville)} onSelectMaison={m=>setDetailMaison(m)} isMobile={isMobile} onGeocoded={handleMaisonGeocoded} />
        ) : <>
        {!isMobile && showBudget && <BudgetWidget budgets={budgets} setBudgets={setBudgets} />}
        {isMobile && mobileTab === "budget" && <BudgetWidget budgets={budgets} setBudgets={setBudgets} />}

        {user && maisons.length===0 && mobileTab !== "budget" && (
          <div style={{ marginBottom:16, padding:"28px 18px", background:"linear-gradient(135deg,rgba(99,102,241,0.07),rgba(167,139,250,0.07))", border:"1px dashed rgba(167,139,250,0.35)", borderRadius:13, textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:10 }}>✨</div>
            <div style={{ fontSize:13, color:"#9ca3af", marginBottom:16 }}>Colle l'URL d'une annonce et on s'occupe du reste — ton premier bien sera prêt à comparer en quelques secondes.</div>
            <button onClick={() => setShowImport(true)} style={{ padding:"11px 22px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#7c3aed,#a78bfa)", color:"#fff", cursor:"pointer", fontWeight:700, fontSize:14 }}>
              Importer ma première annonce
            </button>
          </div>
        )}

        {mobileTab !== "budget" && maisons.length > 0 && (
          <div style={{ display:"flex", gap:7, marginBottom:10, flexWrap:"wrap" }}>
            {[["all","Tous","🏘️"],["residence","Résidence","🏠"],["investissement","Investissement","💸"]].map(([val,lbl,icon]) => (
              <button key={val} onClick={() => { setFilterUsage(val); localStorage.setItem("filterUsage", val); setFilterStatut("Tous"); }}
                style={{ padding:"7px 13px", borderRadius:9, border:"1px solid",
                  borderColor: filterUsage===val ? "rgba(167,139,250,0.5)" : "rgba(255,255,255,0.1)",
                  background: filterUsage===val ? "rgba(167,139,250,0.15)" : "transparent",
                  color: filterUsage===val ? "#c4b5fd" : "#9ca3af",
                  cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>
                {icon} {lbl}
              </button>
            ))}
          </div>
        )}

        {!isMobile && mobileTab !== "budget" && maisons.length > 0 && (
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
            <BatchVerifyButton maisons={maisons} onMaisonVerified={handleMaisonVerified} isMobile={isMobile} />
          </div>
        )}

        {mobileTab !== "budget" && (
          <div style={{ display:"flex", gap:7, marginBottom: isMobile?8:14, flexWrap: isMobile?"nowrap":"wrap", overflowX: isMobile?"auto":"visible" }}>
            <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="🔍 Rechercher..."
              style={{ flex:"1 1 140px", minWidth:120, background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, outline:"none" }} />
            <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)} style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 10px", color:"#fff", fontSize:12, outline:"none" }}>
              <option value="Tous">Tous</option>
              {(filterUsage === "investissement" ? STATUTS_INVEST : filterUsage === "residence" ? STATUTS_RP : [...STATUTS_RP, ...STATUTS_INVEST]).map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"9px 10px", color:"#fff", fontSize:12, outline:"none" }}>
              <option value="dateAjout">Récent</option>
              <option value="prix">Prix ↑</option>
              <option value="prixm2">€/m² ↑</option>
              <option value="surface">Surface ↓</option>
              <option value="ville">Ville</option>
            </select>
          </div>
        )}

        {isMobile && mobileTab !== "budget" && maisons.length > 0 && (
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:12 }}>
            {maisons.length >= 2 && (
              <button onClick={shareComparaison} style={{ padding:"6px 11px", borderRadius:8, border:"1px solid rgba(167,139,250,0.3)", background:"rgba(167,139,250,0.06)", color:"#a78bfa", cursor:"pointer", fontWeight:600, fontSize:11 }}>
                📤 Partager
              </button>
            )}
            {maisons.length > 0 && (
              <BatchVerifyButton maisons={maisons} onMaisonVerified={handleMaisonVerified} isMobile={isMobile} />
            )}
          </div>
        )}

        {mobileTab !== "budget" && (
          filtered.length===0 && maisons.length>0
            ? <div style={{ textAlign:"center", padding:"40px 20px", color:"#4b5563" }}><div style={{ fontSize:32, marginBottom:8 }}>🔍</div><div>Aucun résultat</div></div>
            : <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(290px,1fr))", gap: isMobile?10:16 }}>
                {filtered.map(m => <MaisonCard key={m.id} maison={m} budget={getBudgetForMaison(budgets, m)} onClick={()=>setDetailMaison(m)} onEdit={()=>{setEditingMaison(m);setView("form");}} onDelete={()=>deleteMaison(m.id)} onSimulate={(maison)=>setSimulatingMaison(maison)} onChecklist={(maison)=>setChecklistMaison(maison)} isMobile={isMobile} isSelected={comparison.includes(m.id)} onToggleCompare={()=>toggleCompare(m.id)} />)}
              </div>
        )}
        </>}
      </div>

      {isMobile && (
        <div style={{ position:"fixed", bottom:0, left:0, right:0, height:BOTTOM_NAV_H, background:"#0d0d12", borderTop:"1px solid rgba(255,255,255,0.1)", display:"flex", zIndex:100, transform:"translateZ(0)" }}>
          {[
            { id:"annonces", icon:"🏠", label:"Annonces" },
            { id:"add",      icon:"➕", label:"+ Ajouter" },
            { id:"budget",   icon:"💰", label:"Budget" },
            { id:"carte",    icon:"🗺️", label:"Carte" },
            { id:"compte",   icon: user ? "👤" : "🔐", label:"Compte" },
          ].map(tab => (
            <button key={tab.id} onClick={() => {
              if (tab.id === "add") { setShowAddChoice(true); }
              else if (tab.id === "carte") setMobileTab("carte");
              else if (tab.id === "compte") { if (user) { setShowAccount(true); } else setShowAuth(true); }
              else setMobileTab(tab.id);
            }}
              style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, background:"transparent", border:"none", cursor:"pointer",
                color: mobileTab===tab.id && !["add","compte"].includes(tab.id) ? "#a78bfa" : "#6b7280",
                borderTop: mobileTab===tab.id && !["add","compte"].includes(tab.id) ? "2px solid #a78bfa" : "2px solid transparent",
              }}>
              <span style={{ fontSize:20, height:22, lineHeight:"22px", display:"flex", alignItems:"center", justifyContent:"center" }}>{tab.icon}</span>
              <span style={{ fontSize:10, fontWeight:600, height:12, lineHeight:"12px" }}>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {comparison.length > 0 && view !== "form" && (
        <ComparisonBar
          maisons={maisons}
          ids={comparison}
          isMobile={isMobile}
          bottomOffset={BOTTOM_NAV_H}
          onRemove={(id) => setComparison(prev => prev.filter(x => x !== id))}
          onClear={() => setComparison([])}
          onCompare={() => setShowComparator(true)}
        />
      )}
      {showComparator && (
        <ComparatorView
          maisons={maisons}
          ids={comparison}
          budgets={budgets}
          onClose={() => setShowComparator(false)}
          onRemove={(id) => setComparison(prev => prev.filter(x => x !== id))}
          onShowDetail={(m) => { setShowComparator(false); setDetailMaison(m); }}
        />
      )}
      {showImport && <ImportModal onImport={handleImportDone} onClose={()=>{setShowImport(false);setShareUrl(null);}} initialUrl={shareUrl || ""} />}
      {showAddChoice && (
        <div onClick={()=>setShowAddChoice(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#0f0f13", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"20px 20px 0 0", maxWidth:640, width:"100%", padding:"22px 20px calc(20px + env(safe-area-inset-bottom))" }}>
            <h2 style={{ margin:"0 0 16px", fontSize:17, fontWeight:800, color:"#e2e8f0" }}>Ajouter un bien</h2>
            <button onClick={()=>{ setShowAddChoice(false); setShowImport(true); }} style={{ width:"100%", textAlign:"left", padding:"14px 16px", borderRadius:12, border:"1px solid rgba(99,102,241,0.4)", background:"rgba(99,102,241,0.1)", color:"#818cf8", cursor:"pointer", fontWeight:700, fontSize:15, marginBottom:10 }}>
              🔗 Coller un lien d'annonce
            </button>
            <button onClick={()=>{ setShowAddChoice(false); setEditingMaison(null); setView("form"); }} style={{ width:"100%", textAlign:"left", padding:"14px 16px", borderRadius:12, border:"1px solid rgba(255,255,255,0.15)", background:"rgba(255,255,255,0.03)", color:"#e2e8f0", cursor:"pointer", fontWeight:700, fontSize:15 }}>
              ✏️ Saisir manuellement
            </button>
          </div>
        </div>
      )}
      {detailMaison && <DetailModal maison={detailMaison} budget={getBudgetForMaison(budgets, detailMaison)} onClose={()=>setDetailMaison(null)} onEdit={()=>{setEditingMaison(detailMaison);setDetailMaison(null);setView("form");}} onSimulate={(m)=>{setDetailMaison(null);setSimulatingMaison(m);}} onChecklist={(m)=>{setDetailMaison(null);setChecklistMaison(m);}} onDocuments={(m)=>{setDetailMaison(null);setDocumentsMaison(m);}} />}
      {simulatingMaison && <SimulationModal maison={simulatingMaison} budget={getBudgetForMaison(budgets, simulatingMaison)} onClose={()=>setSimulatingMaison(null)} onSave={async (updated) => {
        setMaisons(prev => prev.map(x => x.id === updated.id ? updated : x));
        if (user) await saveMaisonToSupabase(updated, user.id);
      }} />}
      {checklistMaison && <VisitChecklistModal maison={checklistMaison} onClose={()=>setChecklistMaison(null)} onSave={async (updated) => {
        setMaisons(prev => prev.map(x => x.id === updated.id ? updated : x));
        if (user) await saveMaisonToSupabase(updated, user.id);
      }} />}
      {documentsMaison && <DocumentsModal maison={documentsMaison} user={user} onClose={()=>setDocumentsMaison(null)} onSave={async (updated) => {
        setMaisons(prev => prev.map(x => x.id === updated.id ? updated : x));
        if (user) await saveMaisonToSupabase(updated, user.id);
      }} />}
      {showAuth && <AuthModal onClose={()=>setShowAuth(false)} onShowPrivacy={()=>setShowPrivacy(true)} />}
      {showAccount && user && <AccountModal user={user} syncing={syncing} onClose={()=>setShowAccount(false)} onShowPrivacy={()=>{ setShowAccount(false); setShowPrivacy(true); }} />}
      {showPrivacy && <PrivacyModal onClose={()=>setShowPrivacy(false)} />}
      <FeedbackWidget user={user} currentView={isMobile ? mobileTab : view} isMobile={isMobile} hidden={
        !!(showComparator || showImport || showAddChoice || detailMaison || simulatingMaison || checklistMaison || documentsMaison || showAuth || showAccount || showPrivacy)
      } />
      <div style={{ textAlign:"center", padding:"24px 16px 90px", fontSize:11, color:"#4b5563" }}>
        <button onClick={()=>setShowPrivacy(true)} style={{ background:"none", border:"none", color:"#6b7280", cursor:"pointer", fontSize:11, textDecoration:"underline" }}>
          Politique de confidentialité
        </button>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}
